import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { GET_MENU_CATEGORIES } from "src/constants/queries";
import { set_menu_categories, set_menu_category_loading } from "src/store/features/category-slice";
import { RootState } from "src/store";
import Spinner from "src/components/common/Spinner";
import type { MenuCategory } from "src/interfaces/menu.interface";
import { useRestaurantFromParams } from "src/utils/useRestaurantFromParams";
import { restaurantSegment } from "src/utils/restaurantUrl";
import { useAuth } from "src/contexts/auth-provider";

/**
 * Menu → Items: browse categories to view and manage items.
 * Sidebar max 2 levels; items are accessed via category detail.
 */
export default function MenuItemsListPage() {
  const { hasRestaurant } = useAuth();
  const { restaurantCorrelationId, redirectTo } = useRestaurantFromParams();
  const segment = restaurantCorrelationId ? restaurantSegment(restaurantCorrelationId, hasRestaurant?.name) : "";
  const { axBe } = useAxiosInterceptor();
  const dispatch = useDispatch();
  const { menuCategories, isMenuCategoryLoading } = useSelector((state: RootState) => state.category);

  useEffect(() => {
    if (!restaurantCorrelationId) return;
    dispatch(set_menu_category_loading(true));
    axBe
      .post(GRAPHQL_ENDPOINT, {
        query: GET_MENU_CATEGORIES,
        variables: { storeCorrelationId: restaurantCorrelationId, activeFilter: "ACTIVE" },
      })
      .then(({ data }) => {
        dispatch(
          set_menu_categories({
            categories: data?.data?.categoriesByStoreCorrelationId ?? [],
          })
        );
      })
      .catch(() => {})
      .finally(() => dispatch(set_menu_category_loading(false)));
  }, [restaurantCorrelationId, axBe, dispatch]);

  if (redirectTo) return <Navigate to={redirectTo} replace />;
  if (!restaurantCorrelationId) {
    return (
      <div className="text-sm text-gray-500">
        Select a restaurant to view items.
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl">
      <p className="text-sm text-[#6b7280] mb-4" aria-hidden="true">
        <Link to={`/restaurants/${segment}/menu`} className="hover:text-[#FD7657]">
          Menu
        </Link>
        {" / "}
        <span className="text-[#111827]">Items</span>
      </p>
      <h1 className="text-xl font-semibold text-[#111827] mb-2">Items by category</h1>
      <p className="text-sm text-gray-600 mb-6">
        Open a category to view and manage its items.
      </p>
      {isMenuCategoryLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : menuCategories?.length > 0 ? (
        <ul className="space-y-2">
          {(menuCategories as MenuCategory[]).map((cat: MenuCategory) => (
            <li key={cat.correlationId}>
              <Link
                to={`/restaurants/${segment}/menu/categories/${cat.correlationId}`}
                className="block rounded-lg border border-[#E5E7EB] bg-white px-4 py-3 text-sm font-medium text-[#111827] hover:bg-gray-50 hover:border-[#FD7657]/30"
              >
                {cat.name}
                <span className="ml-2 text-gray-500 font-normal">
                  ({cat.items?.length ?? 0} {cat.items?.length === 1 ? "item" : "items"})
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-gray-500 text-sm py-8">
          No categories yet.{" "}
          <Link
            to={`/restaurants/${segment}/menu`}
            className="text-[#FD7657] font-medium hover:underline"
          >
            Add a category
          </Link>{" "}
          first.
        </p>
      )}
    </div>
  );
}
